// Onglets de la fiche bail (page du module entrée) : Loyers, Charges, Révision, Assurance.
import { useState } from "react";
import { post, put, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money, today } from "@core/format";
import { Badge, Button, Card, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, MoneyInput, Notice, Select, Stat, Table } from "@core/ui";
import { RegularisationForm, RegularisationItem } from "./charges";
import { CallModal, PaymentModal } from "./loyers";
import { IndicesCard, RevisionPanel } from "./revisions";
import { Amount, CallStatus, CoverageStatus, DocLink, METHOD_LABELS, RECEIPT_STATUS, SOURCE_LABELS, useAction, useG, useLeases } from "./shared";

type TabProps = { orgId: string; leaseId: string };

const REASONS: Record<string, string> = { initial: "Conditions d’origine", revision: "Révision", avenant: "Avenant", ajustement_provision: "Ajustement de provision" };

export function LeaseRentTab({ orgId, leaseId }: TabProps) {
  const { g, can } = useG(orgId);
  const q = useGet<any>(g(`/leases/${leaseId}/ledger`));
  const { busy, run } = useAction(orgId);
  const [detail, setDetail] = useState<string | null>(null);
  const [pay, setPay] = useState(false);
  const [adj, setAdj] = useState<{ label: string; cents: number | null; sign: string } | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const b = d.balance;
  return (
    <div className="stack">
      <div className="stats">
        <Stat label="Reste dû échu" value={money(b.overdue_cents)} hint={b.oldest_overdue_on ? `depuis le ${date(b.oldest_overdue_on)}` : undefined} />
        <Stat label="À échoir" value={money(b.upcoming_cents)} />
        <Stat label="Crédit non affecté" value={money(b.credit_cents)} />
      </div>
      <div className="gestion-actions">
        {can("operate") || can("finance") ? (
          <Button variant="secondaire" busy={busy === "gen"} onClick={() => run("gen", () => post<any>(g("/rent-calls/generate"), { lease_id: leaseId }), (r: any) => `${r.outcome.created} échéance(s) créée(s).`)}>
            Générer les échéances
          </Button>
        ) : null}
        {can("finance") ? <Button onClick={() => setPay(true)}>Enregistrer un paiement</Button> : null}
        {can("finance") && b.credit_cents > 0 ? (
          <Button variant="secondaire" busy={busy === "credit"} onClick={() => run("credit", () => post<any>(g(`/leases/${leaseId}/apply-credit`)), (r: any) => `${money(r.applied_cents)} affectés aux échéances ouvertes.`)}>
            Affecter le crédit
          </Button>
        ) : null}
        {can("finance") ? (
          <Button variant="discret" onClick={() => setAdj({ label: "", cents: null, sign: "plus" })}>
            Ajouter une somme ponctuelle
          </Button>
        ) : null}
      </div>
      {adj ? (
        <Card title="Somme ponctuelle à intégrer à la prochaine échéance">
          <div className="form-row">
            <Input label="Libellé" value={adj.label} onChange={(e) => setAdj({ ...adj, label: e.target.value })} />
            <Select label="Nature" value={adj.sign} onChange={(e) => setAdj({ ...adj, sign: e.target.value })} options={[{ value: "plus", label: "Somme due par le locataire" }, { value: "moins", label: "Somme à déduire (en faveur du locataire)" }]} />
            <MoneyInput label="Montant" cents={adj.cents} onChange={(v) => setAdj({ ...adj, cents: v })} />
          </div>
          <div className="gestion-actions">
            <Button busy={busy === "adj"} onClick={async () => { const r = await run("adj", () => post(g(`/leases/${leaseId}/adjustments`), { label: adj.label, amount_cents: (adj.cents ?? 0) * (adj.sign === "moins" ? -1 : 1) }), "Somme enregistrée."); if (r) setAdj(null); }}>
              Enregistrer
            </Button>
            <Button variant="discret" onClick={() => setAdj(null)}>
              Annuler
            </Button>
          </div>
        </Card>
      ) : null}
      <Card title="Échéancier">
        {d.calls.length === 0 ? <Empty title="Aucune échéance">Générez les échéances à partir des conditions du bail.</Empty> : null}
        {d.calls.length ? (
          <Table caption="Échéances du bail">
            <thead>
              <tr>
                <th>Période</th>
                <th>Échéance</th>
                <th className="right">Appelé</th>
                <th className="right">Reste dû</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.calls.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    {c.period_label}
                    {c.details?.prorata ? <span className="block small muted">Prorata {c.details.prorata.days} j / {c.details.prorata.month_days} j</span> : null}
                  </td>
                  <td>{date(c.due_on)}</td>
                  <td className="right">
                    <Amount cents={c.total_cents} />
                  </td>
                  <td className="right">
                    <Amount cents={c.outstanding_cents} />
                  </td>
                  <td>
                    <CallStatus status={c.status} />
                  </td>
                  <td className="right">
                    <Button variant="discret" small onClick={() => setDetail(c.id)}>
                      Détail
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      <Card title="Paiements">
        {d.payments.length === 0 ? <p className="muted">Aucun paiement.</p> : null}
        <ul className="gestion-list">
          {d.payments.map((p: any) => (
            <li key={p.id} className="gestion-line">
              <span>
                {date(p.received_on)} · {METHOD_LABELS[p.method] ?? p.method} · {SOURCE_LABELS[p.source] ?? p.source}
                {p.payer ? ` · ${p.payer}` : ""}
                {p.cancelled_at ? <Badge tone="erreur">Annulé</Badge> : null}
                {p.amount_cents > p.allocated_cents && !p.cancelled_at ? <span className="block small muted">Non affecté : {money(p.amount_cents - p.allocated_cents)}</span> : null}
              </span>
              <Amount cents={p.amount_cents} />
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Quittances et reçus">
        {d.receipts.length === 0 ? <p className="muted">Aucun document.</p> : null}
        <ul className="gestion-list">
          {d.receipts.map((r: any) => (
            <li key={r.id} className="gestion-line">
              <span>
                {r.kind === "quittance" ? "Quittance" : "Reçu"} n° {r.number} · {date(r.period_start)} – {date(r.period_end)} · {money(r.amount_cents)} · {RECEIPT_STATUS[r.status] ?? r.status}
                {r.cancelled_reason ? <span className="block small muted">{r.cancelled_reason}</span> : null}
              </span>
              <DocLink id={r.document_id} />
            </li>
          ))}
        </ul>
      </Card>
      <LeaseSettingsCard orgId={orgId} leaseId={leaseId} settings={d.settings} />
      <Card title="Historique du loyer">
        <Table caption="Historique du loyer">
          <thead>
            <tr>
              <th>Date d’effet</th>
              <th>Motif</th>
              <th className="right">Loyer</th>
              <th className="right">Charges</th>
            </tr>
          </thead>
          <tbody>
            {(d.rent_history.length ? d.rent_history : [{ id: "x", effective_on: d.lease.start_on, reason: "initial", rent_cents: d.lease.rent_cents, charges_cents: d.lease.charges_cents, note: "" }]).map((h: any) => (
              <tr key={h.id}>
                <td>{date(h.effective_on)}</td>
                <td>
                  {REASONS[h.reason] ?? h.reason}
                  {h.note ? <span className="block small muted">{h.note}</span> : null}
                </td>
                <td className="right">
                  <Amount cents={h.rent_cents} />
                </td>
                <td className="right">
                  <Amount cents={h.charges_cents} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      {d.adjustments.length ? (
        <Card title="Sommes ponctuelles">
          <ul className="gestion-list">
            {d.adjustments.map((a: any) => (
              <li key={a.id} className="gestion-line">
                <span>
                  {a.label} · {a.status === "en_attente" ? "en attente d’une échéance" : a.status === "integre" ? "intégrée à une échéance" : a.status === "regle_hors_echeance" ? "réglée hors échéance" : "annulée"}
                </span>
                <span className="gestion-actions">
                  <Amount cents={a.amount_cents} />
                  {a.status === "en_attente" && can("finance") ? (
                    <>
                      <Button variant="discret" small busy={busy === a.id} onClick={() => run(a.id, () => post(g(`/adjustments/${a.id}/settle`), { action: "regle" }), "Marquée comme réglée hors échéance.")}>
                        {a.amount_cents < 0 ? "Remboursée" : "Réglée à part"}
                      </Button>
                      <Button variant="discret" small busy={busy === `x${a.id}`} onClick={() => run(`x${a.id}`, () => post(g(`/adjustments/${a.id}/settle`), { action: "annule" }), "Annulée.")}>
                        Annuler
                      </Button>
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      {detail ? <CallModal orgId={orgId} callId={detail} onClose={() => setDetail(null)} onPay={() => setPay(true)} /> : null}
      {pay ? <PaymentModal orgId={orgId} leaseId={leaseId} onClose={() => setPay(false)} /> : null}
    </div>
  );
}

function LeaseSettingsCard({ orgId, leaseId, settings }: { orgId: string; leaseId: string; settings: any }) {
  const { g, can } = useG(orgId);
  const { busy, run } = useAction(orgId);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  return (
    <Card title="Communication et relances">
      <KeyValues
        items={[
          [
            "Quittances par voie électronique",
            settings.e_receipts_consent
              ? `Accord ${settings.e_receipts_consent_source === "locataire" ? "donné par le locataire depuis son espace" : `déclaré par le bailleur (${settings.e_receipts_consent_note})`} le ${dateTime(settings.e_receipts_consent_at)}`
              : "Pas d’accord enregistré : remise en main propre ou par courrier",
          ],
          ["Relances automatiques", settings.reminders_paused ? `Suspendues : ${settings.reminders_pause_reason}` : "Actives (préparées puis validées par vous)"],
        ]}
      />
      {can("operate") ? (
        <div className="stack-sm">
          {!settings.e_receipts_consent ? (
            <div className="form-row">
              <Input label="Accord recueilli (écrit, date)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. : accord écrit du 12/01/2026" />
              <Button variant="secondaire" small disabled={!note.trim()} busy={busy === "consent"} onClick={() => run("consent", () => put(g(`/leases/${leaseId}/settings`), { e_receipts_consent: true, e_receipts_consent_note: note }), "Accord enregistré.")}>
                Enregistrer l’accord du locataire
              </Button>
            </div>
          ) : (
            <Button variant="discret" small busy={busy === "noconsent"} onClick={() => run("noconsent", () => put(g(`/leases/${leaseId}/settings`), { e_receipts_consent: false }), "Accord retiré.")}>
              Retirer l’accord
            </Button>
          )}
          {settings.reminders_paused ? (
            <Button variant="secondaire" small busy={busy === "resume"} onClick={() => run("resume", () => put(g(`/leases/${leaseId}/settings`), { reminders_paused: false }), "Relances reprises.")}>
              Reprendre les relances
            </Button>
          ) : (
            <div className="form-row">
              <Input label="Motif de suspension" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. : accord en cours de discussion" />
              <Button variant="secondaire" small disabled={!reason.trim()} busy={busy === "pause"} onClick={() => run("pause", () => put(g(`/leases/${leaseId}/settings`), { reminders_paused: true, reminders_pause_reason: reason }), "Relances suspendues.")}>
                Suspendre les relances
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}

export function LeaseChargesTab({ orgId, leaseId }: TabProps) {
  const { g } = useG(orgId);
  const leases = useLeases(orgId);
  const year = new Date().getFullYear() - 1;
  const q = useGet<any>(g(`/charges?year=${year}`));
  const lease = leases.data?.items.find((l) => l.id === leaseId);
  if (leases.isLoading || q.isLoading) return <Loading />;
  const regs = (q.data?.regularisations ?? []).filter((r: any) => r.lease_id === leaseId);
  return (
    <div className="stack">
      {lease?.charges_mode === "forfait" ? (
        <Notice title="Charges forfaitaires">
          Ce bail prévoit un forfait de {money(lease.charges_cents)} par mois : il ne donne lieu ni à régularisation ni à complément (TEOM comprise). Il peut seulement être révisé dans les conditions prévues au bail.
        </Notice>
      ) : (
        <>
          <Notice>Provision mensuelle actuelle : {money(lease?.charges_cents)}. La régularisation compare la part des charges récupérables imputable au locataire aux provisions appelées.</Notice>
          <RegularisationForm orgId={orgId} leases={leases.data?.items ?? []} year={year} leaseId={leaseId} />
        </>
      )}
      <Card title="Régularisations de ce bail">
        {regs.length === 0 ? <p className="muted">Aucune régularisation.</p> : null}
        <ul className="gestion-list">
          {regs.map((r: any) => (
            <RegularisationItem key={r.id} r={r} orgId={orgId} />
          ))}
        </ul>
      </Card>
    </div>
  );
}

export function LeaseRevisionTab({ orgId, leaseId }: TabProps) {
  return (
    <div className="stack">
      <RevisionPanel orgId={orgId} leaseId={leaseId} />
      <details>
        <summary>Indices enregistrés</summary>
        <IndicesCard orgId={orgId} />
      </details>
    </div>
  );
}

export function LeaseInsuranceTab({ orgId, leaseId }: TabProps) {
  const { g, can } = useG(orgId);
  const q = useGet<any>(g(`/leases/${leaseId}/insurance`));
  const { busy, run } = useAction(orgId);
  const [review, setReview] = useState<Record<string, string>>({});
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <div className="stack">
      <Card title="Assurance habitation">
        <p>
          <CoverageStatus state={d.coverage.state} label={d.coverage.label} /> {d.coverage.until ? <span className="small">jusqu’au {date(d.coverage.until)}</span> : null}
        </p>
        <Notice>{d.notice}</Notice>
        {d.certificates.length === 0 ? <Empty title="Aucune attestation">Le locataire peut déposer son attestation depuis son espace.</Empty> : null}
        <ul className="gestion-list">
          {d.certificates.map((c: any) => (
            <li key={c.id}>
              <div className="gestion-line">
                <span>
                  {c.insurer || "Assureur non précisé"} · {c.covers_from ? `du ${date(c.covers_from)} au ${date(c.covers_to)}` : "période non précisée"}
                  <span className="block small muted">
                    Déposée par le {c.uploaded_by_role} le {dateTime(c.created_at)} · {c.status === "validee" ? "vérifiée" : c.status === "a_corriger" ? `à corriger : ${c.review_note}` : "à vérifier"}
                  </span>
                </span>
                <DocLink id={c.document_id}>Attestation</DocLink>
              </div>
              {c.status === "a_verifier" && can("operate") ? (
                <div className="form-row">
                  <Button small busy={busy === `v${c.id}`} onClick={() => run(`v${c.id}`, () => post(g(`/insurance/${c.id}/review`), { status: "validee" }), "Attestation vérifiée.")}>
                    Attestation conforme au logement et à la période
                  </Button>
                  <Input label="Motif de correction" value={review[c.id] ?? ""} onChange={(e) => setReview({ ...review, [c.id]: e.target.value })} placeholder="Ex. : autre adresse, période manquante" />
                  <Button variant="secondaire" small disabled={!review[c.id]} busy={busy === `c${c.id}`} onClick={() => run(`c${c.id}`, () => post(g(`/insurance/${c.id}/review`), { status: "a_corriger", note: review[c.id] }), "Correction demandée.")}>
                    Demander une correction
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
      {can("operate") ? <InsuranceUpload url={g(`/leases/${leaseId}/insurance`)} orgId={orgId} title="Déposer une attestation reçue" /> : null}
    </div>
  );
}

export function InsuranceUpload({ url, title, orgId }: { url: string; title: string; orgId?: string }) {
  const { busy, run } = useAction(orgId);
  const [f, setF] = useState({ covers_from: today(), covers_to: "", insurer: "", policy_number: "" });
  const [file, setFile] = useState<File | null>(null);
  return (
    <Card title={title}>
      <div className="form-row">
        <Input label="Début de la période couverte" type="date" value={f.covers_from} onChange={(e) => setF({ ...f, covers_from: e.target.value })} />
        <Input label="Fin de la période couverte" type="date" value={f.covers_to} onChange={(e) => setF({ ...f, covers_to: e.target.value })} />
      </div>
      <div className="form-row">
        <Input label="Assureur" value={f.insurer} onChange={(e) => setF({ ...f, insurer: e.target.value })} />
        <Input label="Numéro de contrat" value={f.policy_number} onChange={(e) => setF({ ...f, policy_number: e.target.value })} />
      </div>
      <FileDrop onFiles={(fs) => setFile(fs[0])} label="Choisir l’attestation" />
      {file ? <p className="small">Fichier choisi : {file.name}</p> : null}
      <Button
        busy={busy === "up"}
        disabled={!file || !f.covers_to}
        onClick={async () => {
          const form = new FormData();
          form.append("file", file!);
          Object.entries(f).forEach(([k, v]) => form.append(k, v));
          const r = await run("up", () => upload(url, form), "Attestation déposée : elle sera vérifiée.");
          if (r) setFile(null);
        }}
      >
        Déposer l’attestation
      </Button>
    </Card>
  );
}
